import { useState } from "react";
import { toast } from "sonner";
import { HandHeart } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const AREAS = ["Mentorship", "Academic Tutoring", "Athletics / Coaching", "Health & Wellness", "Events", "Operations"];

const inputCls =
  "w-full bg-[#161622] border border-[#242436] focus:border-[#8B5CF6] outline-none text-[#F4F4F6] text-sm px-4 py-3.5 placeholder:text-[#62627A] transition-colors duration-200";

const empty = { name: "", email: "", phone: "", area: "Mentoring", availability: "", skills: "", message: "" };

export const VolunteerForm = () => {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/volunteer", form);
      toast.success("You're in the huddle!", { description: "Our team will reach out within 48 hours." });
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="volunteer" className="py-20 sm:py-28 lg:py-36 px-4 sm:px-8 lg:px-16 bg-[#0C0C12] border-y border-[#242436]" data-testid="volunteer-section">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
        <Reveal className="lg:col-span-2">
          <div className="font-mono text-xs sm:text-sm tracking-[0.3em] uppercase text-[#FFB800] mb-4">Join The Squad</div>
          <h2 className="font-display font-black uppercase tracking-tight leading-none text-3xl sm:text-4xl lg:text-5xl text-[#F4F4F6]">
            Become a <span className="text-[#8B5CF6]">mentor.</span>
          </h2>
          <p className="mt-6 text-sm sm:text-base leading-relaxed text-[#A1A1B3]">
            Coaches, tutors, event crew, role models — GAME runs on people who show up.
            Two hours a week changes a kid's trajectory. Tell us where you fit and we'll get you in the game.
          </p>
          <div className="mt-8 flex items-center gap-4 border border-[#242436] bg-[#111118] p-5">
            <HandHeart className="w-8 h-8 text-[#FFB800] shrink-0" strokeWidth={1.75} />
            <p className="font-mono text-xs uppercase tracking-[0.15em] text-[#A1A1B3] leading-relaxed">
              All volunteers are background-checked and trained before working with youth.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.12} className="lg:col-span-3">
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="volunteer-form">
            <input required placeholder="Full name *" value={form.name} onChange={set("name")} className={inputCls} data-testid="volunteer-name-input" />
            <input required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} data-testid="volunteer-email-input" />
            <input placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="volunteer-phone-input" />
            <select value={form.area} onChange={set("area")} className={inputCls} data-testid="volunteer-area-select">
              {AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            <input placeholder="Availability (e.g. Weekday evenings)" value={form.availability} onChange={set("availability")} className={`${inputCls} sm:col-span-2`} data-testid="volunteer-availability-input" />
            <input placeholder="Skills (e.g. Coaching, math tutoring, photography)" value={form.skills} onChange={set("skills")} className={`${inputCls} sm:col-span-2`} data-testid="volunteer-skills-input" />
            <textarea placeholder="Why do you want to join GAME?" value={form.message} onChange={set("message")} rows={4} className={`${inputCls} sm:col-span-2 resize-none`} data-testid="volunteer-message-input" />
            <button
              type="submit"
              disabled={busy}
              data-testid="volunteer-form-submit"
              className="sm:col-span-2 bg-[#8B5CF6] text-black font-display font-extrabold uppercase tracking-wider text-base px-8 py-4 hover:bg-[#FFB800] active:scale-[0.98] disabled:opacity-50 transition-all duration-200"
            >
              {busy ? "Sending..." : "Sign Me Up"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
